import { SITE } from '../config/siteInfo';

/**
 * Build the schema.org structured data for a city landing page.
 *
 * Returns an array of two nodes injected via <Seo jsonLd={...} />:
 *   1. A `Service` (house cleaning) whose provider is our LocalBusiness (NAP
 *      from the static SITE config), with `areaServed` covering the city and
 *      each neighborhood, plus the city `geo` point.
 *   2. A `BreadcrumbList`: Home → Service Areas → {City}.
 *
 * All values come from the static city record + SITE config so the schema is
 * complete in prerendered HTML (no dependency on the async tenant fetch).
 *
 * @param {Object} city - a record from src/data/serviceAreas.json
 */
export function buildCityJsonLd(city) {
  const provider = {
    '@type': 'LocalBusiness',
    name: SITE.name,
    url: SITE.url,
    telephone: SITE.telephone,
    email: SITE.email,
    image: SITE.ogImage,
    logo: SITE.logo,
    priceRange: SITE.priceRange,
    address: { '@type': 'PostalAddress', ...SITE.address },
    sameAs: SITE.sameAs,
  };

  const areaServed = [
    { '@type': 'City', name: `${city.city}, FL` },
    ...(city.neighborhoods || []).map((n) => ({
      '@type': 'Place',
      name: typeof n === 'string' ? n : n.name,
    })),
  ];

  const service = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'House Cleaning',
    name: `House Cleaning Services in ${city.city}, FL`,
    description: city.metaDescription,
    provider,
    areaServed,
    ...(city.geo
      ? { geo: { '@type': 'GeoCoordinates', latitude: city.geo.lat, longitude: city.geo.lng } }
      : {}),
  };

  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE.url}/` },
      { '@type': 'ListItem', position: 2, name: 'Service Areas', item: `${SITE.url}/service-areas` },
      {
        '@type': 'ListItem',
        position: 3,
        name: `${city.city}, FL`,
        item: `${SITE.url}/house-cleaning/${city.slug}`,
      },
    ],
  };

  return [service, breadcrumb];
}

export default buildCityJsonLd;
